import { uniqBy } from '@belong/common';
import {
  FeatureListingModel,
  MediaModel,
  UnitListingDetailsModel,
} from 'src/api/models';
import { HOUSE_PAGE_STRINGS } from '../../house-page.strings';
import {
  FeatureHighlight,
  ClimateControlHighlight,
  ParkingHighlight,
} from '../../types';
import {
  toDisplayValue,
  parseParkingSpots,
  hasZeroWashers,
  hasNoLaundryAppliances,
} from '../../utils';

export type DisplayFeature = {
  name: string;
  features: FeatureListingModel[]; // A DisplayFeature can wrap multiple house features. These features share the same name.
  media?: MediaModel[];
  isCommunal?: boolean;
};

type CategoryToFeatureMap = Record<string, DisplayFeature[]>;

export function processHomeFeatures(
  home: UnitListingDetailsModel,
): CategoryToFeatureMap {
  const feats = home.features!.filter(
    (feat) =>
      feat.category !== 'Laundry' ||
      feat.name === 'Laundry Hookup' ||
      !hasNoLaundryAppliances(feat),
  );

  // Separate communal features because we format and group features by name
  // A communal feature and a private feature can share the same name, but should be treated as different features
  const { privateFeats, communalFeats } = feats.reduce<{
    privateFeats: FeatureListingModel[];
    communalFeats: FeatureListingModel[];
  }>(
    (prev, feat) => {
      feat.isCommunalFeature
        ? prev.communalFeats.push(feat)
        : prev.privateFeats.push(feat);
      return prev;
    },
    { privateFeats: [], communalFeats: [] },
  );

  // Get display features (features ready to be displayed)
  const categoryToPrivateDisplayFeats = getDisplayFeaturesWithMedia(
    getDisplayFeatures(privateFeats),
    home,
  );
  const categoryToCommunalDisplayFeats = getDisplayFeaturesWithMedia(
    getDisplayFeatures(communalFeats),
    home,
  );

  // Mark communal features (will be displayed differently)
  // `!` throughout this file on `CategoryToFeatureMap` reads: it is a
  // `Record<string, …>`, so `noUncheckedIndexedAccess` widens every lookup to
  // `| undefined` even where — as here — the key came from `Object.keys` of the
  // very same map, or was written on the line above.
  Object.keys(categoryToCommunalDisplayFeats).forEach((category) => {
    categoryToCommunalDisplayFeats[category]!.forEach((displayFeat) => {
      displayFeat.isCommunal = true;
    });
  });

  // Create a featureId to displayFeat map (so we can easily access the displayFeat from the original feature)
  const featToDisplayFeat: Record<string, DisplayFeature> = {};
  const categories = uniqBy(feats.map((feat) => feat.category!));
  categories.forEach((category) => {
    const displayFeats = (categoryToPrivateDisplayFeats[category] || []).concat(
      categoryToCommunalDisplayFeats[category] || [],
    );
    displayFeats.forEach((displayFeat) => {
      displayFeat.features.forEach(
        (feat) => (featToDisplayFeat[feat.id!] = displayFeat),
      );
    });
  });

  // Loop trough the ordered features and add the corresponding displayFeat to the new object
  const categoryToDisplayFeats: CategoryToFeatureMap = {};
  feats.forEach((feat) => {
    const displayFeat = featToDisplayFeat[feat.id!]!;
    const category = feat.category!;
    if (!categoryToDisplayFeats[category])
      return (categoryToDisplayFeats[category] = [displayFeat]);
    if (!categoryToDisplayFeats[category]!.includes(displayFeat))
      categoryToDisplayFeats[category]!.push(displayFeat);
  });
  return categoryToDisplayFeats;
}

// Format features and return display features. This function does not add media to the display feature.
function getDisplayFeatures(
  features: FeatureListingModel[],
): CategoryToFeatureMap {
  const categoryToFeats = features.reduce<
    Record<string, FeatureListingModel[]>
  >((prev, feat) => {
    const category = feat.category!;
    prev[category] ? prev[category].push(feat) : (prev[category] = [feat]);
    return prev;
  }, {});

  const categoryToDisplayFeats: CategoryToFeatureMap = {};
  Object.keys(categoryToFeats).forEach((category) => {
    const features = categoryToFeats[category]!;
    let displayFeats: DisplayFeature[];

    switch (category) {
      case 'Accessibility':
      case 'Pool':
      case 'Other':
        displayFeats = countFeatures(groupFeatures(features));
        break;
      case 'Lighting':
        displayFeats = countFeatures(groupFeatures(features), ['Sun Tunnel']);
        break;
      case 'Utilities':
        displayFeats = countFeatures(groupFeatures(features), ['Water Heater']);
        break;
      case 'Storage':
        displayFeats = countFeatures(groupFeatures(features), [
          'Medicine Cabinet',
        ]);
        break;
      case 'Yard':
        displayFeats = countFeatures(groupFeatures(features), [
          'Fire Pit',
          'Courtyard',
          'Patio',
          'Deck',
          'Garden',
          'Bike Rack',
        ]);
        break;
      case 'Surroundings':
      case 'Safety & Security':
      case 'Fitness':
      case 'Audio & Video':
      case 'Aesthetics':
      default:
        displayFeats = groupFeatures(features);
        break;
      case 'Parking':
        displayFeats = groupFeatures(
          formatFeatures(features, formatParkingFeature),
        );
        break;
      case 'Climate Control':
        displayFeats = groupFeatures(
          formatFeatures(features, formatClimateControlFeature),
        );
        break;
      case 'Laundry':
        displayFeats = groupFeatures(
          formatFeatures(features, formatLaundryFeature),
        );
        break;
      case 'Cooking':
        displayFeats = groupFeatures(
          formatFeatures(features, formatCookingFeature),
        );
        break;
    }
    categoryToDisplayFeats[category] = displayFeats;
  });

  return categoryToDisplayFeats;
}

// Add media (photos) to the display features
function getDisplayFeaturesWithMedia(
  categoryToDisplayFeatures: CategoryToFeatureMap,
  home: UnitListingDetailsModel,
): CategoryToFeatureMap {
  const featMedia = home.tourMedia!.filter(
    (media) => media.ownerType === 'Feature',
  );

  Object.keys(categoryToDisplayFeatures).forEach((category) => {
    categoryToDisplayFeatures[category]!.forEach((displayFeat) => {
      const featIds = displayFeat.features.map((feat) => feat.id);
      // The `!!media.ownerId` guard is not a behaviour change: `ownerId` is
      // optional on the model and `featIds` never contains `undefined`, so the
      // `includes` was already false for that case.
      const media = featMedia.filter(
        (media) => !!media.ownerId && featIds.includes(media.ownerId),
      );
      if (media.length) displayFeat.media = media;
    });
  });

  return categoryToDisplayFeatures;
}

// A house can contain features with the same name. No duplicate features should be displayed, so they are grouped into a single DisplayFeature
function groupFeatures(features: FeatureListingModel[]): DisplayFeature[] {
  const featNameToDisplayFeat: Record<string, DisplayFeature> = {};

  features.forEach((feat) => {
    const featName = feat.name!;
    if (featNameToDisplayFeat[featName]) {
      featNameToDisplayFeat[featName].features.push(feat);
    } else {
      featNameToDisplayFeat[featName] = { features: [feat], name: featName };
    }
  });

  return Object.keys(featNameToDisplayFeat).map(
    (featName) => featNameToDisplayFeat[featName]!,
  );
}

// Counts how many grouped (repeated) features there are and display them in the feature name
function countFeatures(
  displayFeatures: DisplayFeature[],
  countableFeatures?: string[],
): DisplayFeature[] {
  return displayFeatures.map((displayFeat) => {
    const featuresName = displayFeat.features[0]!.name!;
    const shouldCount =
      !countableFeatures || countableFeatures.includes(featuresName);
    const count = shouldCount ? displayFeat.features.length : 0;

    if (count > 1) {
      displayFeat.name = `${count} ${displayFeat.name}s`;
    }

    return displayFeat;
  });
}

// Format helper
function formatFeatures(
  features: FeatureListingModel[],
  formatter: (feature: FeatureListingModel) => string,
): FeatureListingModel[] {
  return features.map((feat) => {
    const newFeat = { ...feat };
    newFeat.name = formatter(feat);
    return newFeat;
  });
}

// Maps parkingType attribute values from the generic "Parking" feature to display names
const parkingTypeToDisplay: Record<string, string> = {
  garage: 'Car Garage',
  driveway: 'Driveway Parking',
  lot: 'Lot Parking',
  carport: 'Carport Parking',
};

// 'format{category}Feat' format the name of a feature (the feature has to be in a certain category)
function formatParkingFeature(feature: FeatureListingModel): string {
  const featName = feature.name! as ParkingHighlight;
  const { attributes = {} } = feature;
  const spots = parseParkingSpots(attributes);

  // Handle new inspection's generic "Parking" feature (uses parkingType attribute)
  const parkingType = (attributes.parkingType || attributes.ParkingType) as
    | string
    | undefined;
  if (featName === 'Parking' && parkingType) {
    const display = parkingTypeToDisplay[parkingType] ?? 'Parking';
    return spots ? `${spots} ${display}` : display;
  }

  switch (featName) {
    case 'Driveway Parking':
    case 'Lot Parking':
    case 'Carport Parking':
      return spots ? `${spots} ${featName}` : featName;
    case 'Garage Parking':
      return spots ? `${spots} Car Garage` : 'Car Garage';
    case 'Charging Station':
      return attributes.IsOutletOnly
        ? 'Electric Vehicle Outlet'
        : 'Charging Station';
    case 'Alley / Off-Street Parking':
    case 'On-Street Permit':
    default:
      return featName;
  }
}

function formatClimateControlFeature(feature: FeatureListingModel): string {
  const featName = feature.name! as ClimateControlHighlight;
  const { attributes = {} } = feature;

  const heatType =
    toDisplayValue(attributes.heatType || attributes.HeatType) ||
    toDisplayValue(attributes.heatSource || attributes.HeatSource);
  const heatDistribution = toDisplayValue(
    attributes.heatDistribution || attributes.HeatDistribution,
  );
  const isCentral =
    attributes.isCentralAirCondition ||
    attributes.IsCentralAirCondition ||
    attributes.isCentralSystem === 'yes' ||
    attributes.IsCentralSystem === 'yes';
  const isFunctional =
    attributes.isFunctional ??
    attributes.IsFunctional ??
    (attributes.fireplaceType || attributes.FireplaceType) === 'working';
  const thermostatType = attributes.type || attributes.Type;

  switch (featName) {
    case 'Heat':
      if (heatType && heatDistribution)
        return `Heating: ${heatType} (${heatDistribution})`;
      if (heatType) return `Heating: ${heatType}`;
      if (heatDistribution) return `Heating: (${heatDistribution})`;
      return featName;
    case 'A/C':
      return isCentral ? 'Cooling: Central' : featName;
    case 'Thermostat':
      return thermostatType === 'Smart' ||
        thermostatType === 'smart' ||
        attributes.thermostatType === 'smart' ||
        attributes.ThermostatType === 'smart'
        ? 'Smart Thermostat'
        : featName;
    case 'Fireplace':
      return isFunctional ? featName : 'Fireplace (Decorative Only)';
    default:
      return featName;
  }
}

function formatCookingFeature(feature: FeatureListingModel): string {
  const featName = feature.name! as FeatureHighlight;
  const { attributes = {} } = feature;
  const type = attributes?.Type;

  if (featName === 'Hood' && type) {
    if (type === 'Vented') return 'Vented Hood';
    return `${featName} (${type})`;
  }

  return featName;
}

function formatLaundryFeature(feature: FeatureListingModel): string {
  if (feature.name === 'Laundry Hookup') return 'Hookups Only';

  const { isCommunalFeature, attributes = {} } = feature;
  const {
    communalLaundryType,
    CommunalLaundryType,
    canBeConcealed,
    CanBeConcealed,
  } = attributes;
  const comLaundryType =
    communalLaundryType ||
    CommunalLaundryType ||
    toDisplayValue(attributes.LaundryPriceModality);
  const concealed = canBeConcealed || CanBeConcealed;

  if (isCommunalFeature) {
    return comLaundryType ? `Communal (${comLaundryType})` : 'Communal';
  }
  const appliances = hasZeroWashers(feature) ? 'Dryer' : 'Washer/Dryer';
  return `${concealed ? 'Enclosed ' : ''}${appliances}`;
}

export function getMediaFeatures(
  categoryToDisplayFeatures: CategoryToFeatureMap,
  category: string,
) {
  return categoryToDisplayFeatures[category]!.filter(
    (displayFeat) => displayFeat.media,
  ).map((cat) => {
    // `!`: `media` is exactly what the `filter` above kept.
    const media = cat.media!.map((media) => {
      const mediaFeature = cat.features.find(
        (feat) => feat.id === media.ownerId,
      );
      return {
        ...media,
        isCommunal: mediaFeature?.isCommunalFeature,
        imageName: mediaFeature?.name,
      };
    });
    return { ...cat, media };
  });
}

export function getNonMediaFeatureNames(
  categoryToDisplayFeatures: CategoryToFeatureMap,
  category: string,
) {
  return categoryToDisplayFeatures[category]!.filter(
    (displayFeat) => !displayFeat.media,
  ).map((displayFeat) => {
    let name = displayFeat.name;
    if (displayFeat.isCommunal && !name.toLowerCase().includes('communal')) {
      name = `${name} (${HOUSE_PAGE_STRINGS['communal.display']})`;
    }
    return name;
  });
}
